import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Textarea,
} from "@repo/ui";
import { useState } from "react";

type Cat = {
  id: string;
  name: string;
  breed: string;
  age: string;
  image: string;
  traits: string[];
};

const CATS: Cat[] = [
  {
    id: "bsh-ginger",
    name: "Amber",
    breed: "British Shorthair",
    age: "12 weeks",
    image: "https://images.pexels.com/photos/16385321/pexels-photo-16385321.jpeg",
    traits: ["Calm", "Cuddly", "Round face"],
  },
  {
    id: "ragdoll-blue",
    name: "Cloud",
    breed: "Ragdoll",
    age: "10 weeks",
    image: "https://images.pexels.com/photos/11489968/pexels-photo-11489968.jpeg",
    traits: ["Gentle", "Blue eyes", "Affectionate"],
  },
  {
    id: "siam-sleek",
    name: "Milo",
    breed: "Siamese",
    age: "11 weeks",
    image: "https://images.pexels.com/photos/28128245/pexels-photo-28128245.jpeg",
    traits: ["Vocal", "Playful", "Curious"],
  },
];

export default function CatsPage() {
  return (
    <div className="min-h-screen">
      <header className="px-6 md:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3 select-none">
          <img src="/paw.svg" alt="Warm Cats" className="h-8 w-8" />
          <span className="text-xl font-semibold tracking-tight">Warm Cats</span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 pb-20">
        <section className="py-4 md:py-6">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Available cats</h1>
          <p className="text-muted-foreground mt-2">Curated, healthy kittens raised with care.</p>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {CATS.map((cat) => (
            <CatCard key={cat.id} cat={cat} />
          ))}
        </section>

        <section className="mt-14 md:mt-20 rounded-3xl bg-secondary/60 p-6 md:p-10">
          <h2 className="text-xl md:text-2xl font-semibold">Our care promise</h2>
          <p className="text-muted-foreground mt-2 max-w-3xl">
            Every kitten is vet-checked, vaccinated, litter-trained, and well socialized. We provide
            a starter kit, health documentation, and ongoing guidance to ensure a smooth transition
            to their new home.
          </p>
          <ul className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-foreground/80">
            <li>• Transparent medical records</li>
            <li>• Early socialization and enrichment</li>
            <li>• Nutrition and settling-in guide</li>
          </ul>
        </section>
      </main>

      <footer className="px-6 md:px-10 pb-10 text-sm text-muted-foreground">
        © {new Date().getFullYear()} Warm Cats — ethical, transparent, and caring.
      </footer>
    </div>
  );
}

function CatCard({ cat }: { cat: Cat }) {
  return (
    <Card className="overflow-hidden rounded-3xl border-border/60">
      <div className="aspect-[4/3] overflow-hidden">
        <img
          src={cat.image}
          alt={`${cat.name} the ${cat.breed}`}
          className="w-full h-full object-cover"
        />
      </div>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">{cat.name}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center justify-between text-sm">
          <span className="text-foreground/80">{cat.breed}</span>
          <span className="text-muted-foreground">{cat.age}</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {cat.traits.map((t) => (
            <Badge
              key={t}
              variant="secondary"
              className="rounded-xl bg-secondary text-secondary-foreground"
            >
              {t}
            </Badge>
          ))}
        </div>
        <div className="mt-4">
          <InquiryDialog cat={cat} />
        </div>
      </CardContent>
    </Card>
  );
}

function InquiryDialog({ cat }: { cat: Cat }) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: `I'm interested in ${cat.name} (${cat.breed}).`,
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    // Placeholder: integrate API later

    console.log("Inquiry submitted", { cat: cat.id, ...form });
    setSent(true);
    setTimeout(() => setOpen(false), 1200);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setSent(false);
      }}
    >
      <DialogTrigger asChild>
        <Button className="w-full rounded-xl bg-primary text-primary-foreground hover:opacity-90">
          Inquire
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-3xl">
        <DialogHeader>
          <DialogTitle>Adoption inquiry</DialogTitle>
          <DialogDescription>
            Tell us a little about you, and we’ll get back with next steps.
          </DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="py-6 text-center text-foreground">
            Thank you — we’ll be in touch shortly.
          </div>
        ) : (
          <form className="space-y-4" onSubmit={submit}>
            <div className="grid gap-2">
              <Label htmlFor="cat">Cat</Label>
              <Input id="cat" value={`${cat.name} • ${cat.breed}`} readOnly />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="name">Your name</Label>
              <Input
                id="name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
            </div>
            <div className="pt-2">
              <Button
                type="submit"
                className="w-full rounded-xl bg-primary text-primary-foreground"
              >
                Send inquiry
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
